// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// Shared chart-spec schema for generative charts (T-302). The backend emits a
// validated Recharts config; we re-validate on the client with Zod before
// rendering so a malformed spec degrades gracefully instead of crashing.

import { z } from "zod";

export const chartTypeSchema = z.enum(["line", "bar", "area", "pie", "scatter"]);
export type ChartType = z.infer<typeof chartTypeSchema>;

export const seriesSchema = z.object({
  key: z.string(),
  label: z.string().optional(),
  color: z.string().nullable().optional(),
});

export const chartSpecSchema = z.object({
  type: chartTypeSchema,
  title: z.string().nullable().optional(),
  x: z.object({ key: z.string(), label: z.string().optional() }),
  series: z.array(seriesSchema).min(1),
  stacked: z.boolean().optional().default(false),
  data: z.array(z.record(z.any())).min(1),
});

export type ChartSpec = z.infer<typeof chartSpecSchema>;

export function parseChartSpec(input: unknown): ChartSpec | null {
  const result = chartSpecSchema.safeParse(input);
  return result.success ? result.data : null;
}

// Default palette aligned with the app's pine/green tokens.
export const CHART_COLORS = [
  "#2f6f4e",
  "#4f9d69",
  "#8ec07c",
  "#d4a373",
  "#6b9080",
  "#a3b18a",
  "#386641",
  "#bc6c25",
];
